import { ObjectId } from 'bson';
import { Assistant } from 'openai/resources/beta/assistants';
import { AdminMessage, BotMessageFeedback, CustomToolKitTypeItem, GlobalStat, HistoryItemContext, HistoryItemResponse, MID, RequestStatEntry, SDATE, SearchDoc, ServerChatbotConfig, SkillState, UsageStat } from './index.js';
import { KeyValEntry } from './KeyValStore.js';
import { ProjectKeyValEntry } from './ProjectKeyValStore.js';
import { User } from "./User.js";

export type ServerToClientEvents<ID extends MID = string, D = string> = {
  //? Neue User Nachricht wir gebroadcastet
  "supervisorMessage": (data: { message: AdminMessage<ID> }) => void,
  "addUserMessage": (data: { message: HistoryItemResponse<ID> }) => void,
  "updateFeedback": (data: { feedback: BotMessageFeedback<ID> }) => void,
  "deleteFeedback": (data: { feedback: BotMessageFeedback<ID> }) => void,
  "createMessagesInThread": (data: { messages: HistoryItemResponse<ID>[] }) => void,
  "deleteMessages": (data: { messages: HistoryItemResponse<ID>[] }) => void,
  //? Assistant Nachricht wird aktualisiert
  "runComplete": (data: HistoryItemResponse<ID>) => void,
  "runUpdate": (data: Omit<HistoryItemResponse<ID>, "content"> & { snapshot: string, deltaStr: string, item_id: string }) => void,
  "runFailed": (message: string) => void,
  "runIncomplete": (message: string) => void,
  //? Initial Daten nach dem Connecten zum Socket
  "stateUpdate": (data: { sessionState: SessionState }) => void,
  "keyValUpdate": (data: KeyValEntry<unknown, ID, D>) => void,
  "keyValDelete": (data: KeyValEntry<unknown, ID, D>) => void,
  "projectKeyValUpdate": (data: ProjectKeyValEntry<unknown, ID, D>) => void,
  "projectKeyValDelete": (data: ProjectKeyValEntry<unknown, ID, D>) => void,

  //? Content auf Basis des ausgeführten Tools
  "customToolKitExec": (data: { type: CustomToolKitTypeItem, content: string }) => void,
  "debugMsg": (data: { stat: UsageStat | GlobalStat | RequestStatEntry }) => void,
  "supervisorExec": (data: { js: string }, cb: (result: unknown) => void) => void,
  "logoutoldsession": (data: { oldSessionId: string }) => void,
  "skillUsed": (data: SkillState) => void,
  "thereCanOnlyBeOne": () => void,
  "userDocUploadStatus": (data: { status: "uploading" | "complete" | "error", filename: string, subStatus: string, doc?: SearchDoc, message?: string }) => void,
  "didSpeak": (data: { text: string }) => void,
  "reflactTrainAuthInvalid": (data: { mcpId: ID }) => void,

};

export type ClientToServerEvents<ID = string, D = string> = {
  //? Registriert für debugmesssages
  "registerDebug": (data: { register: boolean; }, cb: (result: { currentStats: (UsageStat | GlobalStat | RequestStatEntry)[] }) => void) => void,
  "registerAvatarSession": (data: { sessionId: string }) => void,
  "storeKeyValUpdate": (data: Pick<KeyValEntry<any, string, string>, "key" | "value" | "type">, cb: (result: KeyValEntry<unknown, ID, D>) => void) => void,
  "removeKeyVal": (data: Pick<KeyValEntry<any, string, string>, "key" | "type">, cb: (result: KeyValEntry<unknown, ID, D>) => void) => void,
  "getInitialKeyVals": (cb: (result: KeyValEntry<unknown, ID, D>[]) => void) => void,
  "storeProjectKeyValUpdate": (data: Pick<ProjectKeyValEntry<any, string, string>, "key" | "value" | "type">, cb: (result: ProjectKeyValEntry<unknown, ID, D>) => void) => void,
  "removeProjectKeyVal": (data: Pick<ProjectKeyValEntry<any, string, string>, "key" | "type">, cb: (result: ProjectKeyValEntry<unknown, ID, D>) => void) => void,
  "getInitialProjectKeyVals": (cb: (result: ProjectKeyValEntry<unknown, ID, D>[]) => void) => void,
  "addStatisticCount": (data: { stat: 'string' }) => void,
  //? Blockly RAGAI.DEBUG.log() Aufruf, wird als debugMsg an die Debug-View weitergeleitet
  "blocklyDebugLog": (data: { title: string; reason: string }) => void,
  //? RAGAI-Event (nur erlaubte Prefixe, siehe RAGAI_DEBUG_EVENT_PREFIXES), wird als debugMsg weitergeleitet
  "logRagaiEvent": (data: { hook: string; reason: string }) => void,
};

export type ServerToServerEvents = {};
export const blockedStatesArr = ["unknown", "init_assistant", "init_thread", "init_files", "requesting_tools", "thinking", "requesting_websearch", "generating_audio", "speaking"] as const;
export const idleStatesArr = ["idle", "openaierror"] as const
export const sessionStatesArr = [...idleStatesArr, ...blockedStatesArr] as const;
export type SessionState = typeof sessionStatesArr[number];

export const isIdleState = (state: SessionState) => {
  //@ts-ignore
  return idleStatesArr.includes(state);
}

export type SocketData = {
  user: User<ObjectId, Date>,
  botId: string,
  groupId: string
  botConfig: ServerChatbotConfig,
  context: HistoryItemContext,
  assistant: Assistant,
  threadId: string,
  avatarSession?: string
};

export type SocketEvent<ID extends MID = string, DATE extends SDATE = string> = {
  _id: ID,
  created: DATE,
  context: HistoryItemContext,
  openAiThreadId: string,
  type: 'connect' | 'disconnect',
  /** Close reason (e.g. "transport close", "heartbeat-timeout", "client disconnect"). Only set for `type: 'disconnect'`. */
  reason?: string
};
